import React, { useEffect, useState, VFC } from 'react';

import classNames from 'classnames';
import {
  FacebookEmbed,
  InstagramEmbed,
  LinkedInEmbed,
  PinterestEmbed,
  TikTokEmbed,
  XEmbed,
  YouTubeEmbed,
} from 'react-social-media-embed';

import BlockedSocialPost from '/components/BlockedSocialPost';
import { SocialMediaContentJson, SocialMediaPlatformType } from '/types';

import styles from './SocialMediaColumn.scss';

export const FACEBOOK_SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';

const SOCIAL_MEDIA_RATIOS = {
  // width / height
  facebook: 1,
  linkedin: 3 / 4,
  pinterest: 1 / 2.2,
  instagram: 2 / 3,
  twitter: 4 / 4.92,
};

export const calculateSocialMediaDimensions = (
  columnHeight: number | undefined,
  columnWidth: number | undefined,
  provider: SocialMediaPlatformType,
): { height?: number | string; width?: number | string } => {
  // Tiktoks have a fixed height and width. Allow them to take up as much
  // space as they need.
  if (provider === 'tiktok') {
    return {};
  }

  // Youtube will resize itself cropping the preview and scaling down the viedo to
  // fit with the right aspect ratio. Give it a dynamic width and full height.
  if (provider === 'youtube') {
    return {
      width: '100%',
      height: columnHeight,
    };
  }

  if (columnHeight === undefined || columnWidth === undefined) {
    return {
      width: columnWidth,
      height: columnHeight,
    };
  }

  const ratio = SOCIAL_MEDIA_RATIOS[provider];
  const fitWidth = Math.floor(Math.min(columnWidth, columnHeight * ratio));

  // Instagram's smallest post size is taller than most of our media blocks. Still scale down
  // the width but allow it to take up whatever height it needs.
  // Facebook and Twitter scale correctly with just the width so set that.
  if (['instagram', 'facebook', 'twitter'].includes(provider)) {
    return {
      width: fitWidth,
    };
  }

  const fitHeight = Math.floor(Math.min(columnHeight, columnWidth / ratio));

  return {
    width: fitWidth,
    height: fitHeight,
  };
};

const useIsPostBlocked = (
  link: string,
  provider: string,
  timeout: number,
): boolean => {
  const [postFailedToLoad, setPostFailedToLoad] = useState<boolean>(false);
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPostFailedToLoad(true);
    }, timeout);

    const urlsToCheck: string[] = [];

    // Facebook restricts requests for the link url to pages with the same origin as the url. Also while testing
    // with the shanghai proxy these facebook links were fetched ok so it's not a good indicator if it's blocked.
    // The embedder fetches some javascript from connect.facebook.com that is blocked when testing this with the
    // shanghai proxy and isn't limited to the same origin. Check that instead.
    if (provider === 'facebook') {
      urlsToCheck.push(FACEBOOK_SDK_URL);
    } else {
      urlsToCheck.push(link);
    }

    Promise.all(urlsToCheck.map((url) => fetch(url, { mode: 'no-cors' })))
      .catch(() => {
        setPostFailedToLoad(true);
      })
      .then(() => {
        clearTimeout(timeoutId);
      });
  }, [link, provider, timeout]);

  return postFailedToLoad;
};

const SocialMediaColumn: VFC<SocialMediaContentJson & { timeout?: number }> = ({
  provider,
  link,
  media_height,
  media_width,
  timeout = 5000,
}) => {
  const postBlocked = useIsPostBlocked(link, provider, timeout);

  const commonArgs = {
    ...calculateSocialMediaDimensions(media_height, media_width, provider),
    url: link,
  };

  const embedComponents: { [key: string]: JSX.Element } = {
    facebook: <FacebookEmbed {...commonArgs} />,
    instagram: <InstagramEmbed {...commonArgs} captioned />,
    linkedin: <LinkedInEmbed {...commonArgs} />,
    pinterest: <PinterestEmbed {...commonArgs} />,
    tiktok: <TikTokEmbed {...commonArgs} />,
    twitter: <XEmbed {...commonArgs} />,
    youtube: <YouTubeEmbed {...commonArgs} />,
  };

  return (
    <div
      className={classNames(styles.socialMedia, {
        [styles.facebook]: provider === 'facebook',
      })}
      data-testid={`${provider}-embed`}
    >
      {postBlocked ? (
        <div
          style={{
            width: commonArgs.width,
            height: commonArgs.height,
            minWidth: '250px',
          }}
        >
          <BlockedSocialPost provider={provider} link={link} />
        </div>
      ) : (
        embedComponents[provider]
      )}
    </div>
  );
};

export default SocialMediaColumn;
